import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';
import GoldenScissors from '@/components/GoldenScissors';
import PasswordRequirements, { PasswordMatch } from '@/components/PasswordRequirements';
import api from '@/services/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReset, setCaptchaReset] = useState(0);
  const turnstileRef = useRef(null);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-6 hero-gradient">
        <Card className="w-full max-w-md border border-border/40 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <h2 className="font-cormorant text-2xl text-primary">Invalid Reset Link</h2>
            <p className="text-muted-foreground text-sm">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/forgot-password">
              <Button className="btn-primary">Request New Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Challenge runs NOW (not at page load) — token is always fresh at submit.
      const captcha = (await turnstileRef.current?.getToken()) || captchaToken;
      const { data } = await api.post('/auth/reset-password', { token, new_password: password, turnstile_token: captcha });
      // They proved email ownership and just set the password — sign them straight
      // in instead of bouncing back to the login form. PREMIUM.
      if (data.access_token && data.user) {
        login(data.access_token, data.user, true);
        toast.success('Password reset — welcome back!');
        const role = data.user.role;
        navigate(role === 'stylist' ? '/stylist/dashboard' : role === 'admin' ? '/admin' : '/services');
        return;
      }
      setIsSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
      // Fresh single-use Turnstile token for the retry.
      setCaptchaReset((n) => n + 1);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-6 hero-gradient">
        <Card className="w-full max-w-md border border-border/40 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center border border-primary/30">
              <GoldenScissors size="md" />
            </div>
            <h2 className="font-cormorant text-2xl text-primary">Password Reset</h2>
            <p className="text-muted-foreground text-sm">
              Your password has been updated successfully. You can now sign in with your new password.
            </p>
            <Button
              className="btn-primary w-full"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6 hero-gradient">
      <Card className="w-full max-w-md border border-border/40 shadow-lg">
        <CardHeader className="space-y-2">
          <CardTitle className="font-cormorant text-3xl text-center text-primary">Reset Password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="new-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`bg-card border-border pr-10 ${showPassword ? '' : 'onda-password-mask'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordRequirements password={password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                name="confirm-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`bg-card border-border ${showPassword ? '' : 'onda-password-mask'}`}
              />
              <PasswordMatch password={password} confirm={confirmPassword} />
            </div>

            <TurnstileWidget ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} resetSignal={captchaReset} />

            <Button type="submit" className="w-full btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center">
              <ArrowLeft className="mr-1 h-3 w-3" />
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
